import { type IWalletTransaction } from '@/store/api';

import {
	INSIGHTS_WINDOW_DAYS,
	RUNWAY_HEALTHY_DAYS,
	RUNWAY_LOW_DAYS,
	SPARKLINE_POINTS,
	TRANSACTION_TYPE_LABELS,
} from './constants';

export type TransactionTypeKey = 'unknown' | 'credit' | 'debit';

export interface TransactionTypeMeta {
	index: number;
	key: TransactionTypeKey;
	label: string;
	tone: 'success' | 'danger' | 'warning' | 'info' | 'neutral' | 'muted';
	directionLabel: string;
}

const META_BY_INDEX: TransactionTypeMeta[] = [
	{ index: 0, key: 'unknown', label: 'Unknown', tone: 'muted', directionLabel: '—' },
	{ index: 1, key: 'credit', label: 'Credit', tone: 'success', directionLabel: 'In' },
	{ index: 2, key: 'debit', label: 'Debit', tone: 'danger', directionLabel: 'Out' },
];

export const transactionTypeMeta = (index: number): TransactionTypeMeta => META_BY_INDEX[index] ?? META_BY_INDEX[0];

export const transactionTypeOptions = META_BY_INDEX.filter((m) => m.index !== 0).map((m) => ({
	value: String(m.index),
	label: m.label,
}));

export const formatCreditNaira = (credits: number, nairaPerCredit?: number) => {
	if (!nairaPerCredit || !Number.isFinite(nairaPerCredit) || nairaPerCredit <= 0) return null;
	const naira = credits * nairaPerCredit;
	return `≈ ₦${naira.toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;
};

const startOfDayUtc = (d: Date) => {
	const utc = new Date(d.getTime());
	utc.setUTCHours(0, 0, 0, 0);
	return utc;
};

const dayKey = (iso: string) => {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return null;
	return startOfDayUtc(d).toISOString().slice(0, 10);
};

const labelFromKey = (key: string) => {
	const [, m, day] = key.split('-');
	return `${day}/${m}`;
};

export interface DailyInOutPoint {
	dateKey: string;
	label: string;
	creditIn: number;
	creditOut: number;
}

export const aggregateLast30Days = (transactions: IWalletTransaction[]): DailyInOutPoint[] => {
	const today = startOfDayUtc(new Date());
	const map = new Map<string, DailyInOutPoint>();

	for (let i = INSIGHTS_WINDOW_DAYS - 1; i >= 0; i -= 1) {
		const d = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
		const key = d.toISOString().slice(0, 10);
		map.set(key, { dateKey: key, label: labelFromKey(key), creditIn: 0, creditOut: 0 });
	}

	transactions.forEach((tx) => {
		const key = dayKey(tx.createdAt);
		if (!key) return;
		const point = map.get(key);
		if (!point) return;
		const meta = transactionTypeMeta(tx.transactionType);
		const amount = Math.abs(tx.amount);
		if (meta.key === 'credit') point.creditIn += amount;
		else if (meta.key === 'debit') point.creditOut += amount;
	});

	return Array.from(map.values());
};

export interface ServiceBreakdownEntry {
	serviceType: string;
	totalSpent: number;
	transactionCount: number;
}

export const aggregateByServiceType = (transactions: IWalletTransaction[], topN = 5): ServiceBreakdownEntry[] => {
	const map = new Map<string, ServiceBreakdownEntry>();

	transactions.forEach((tx) => {
		const meta = transactionTypeMeta(tx.transactionType);
		if (meta.key !== 'debit') return;
		const key = tx.serviceType || 'Unknown service';
		const entry = map.get(key) ?? { serviceType: key, totalSpent: 0, transactionCount: 0 };
		entry.totalSpent += Math.abs(tx.amount);
		entry.transactionCount += 1;
		map.set(key, entry);
	});

	return Array.from(map.values())
		.sort((a, b) => b.totalSpent - a.totalSpent)
		.slice(0, topN);
};

export interface RunwaySummary {
	dailyBurn: number;
	runwayDays: number | null;
	level: 'healthy' | 'low' | 'critical' | 'idle';
	label: string;
}

export const computeRunway = (currentBalance: number, transactions: IWalletTransaction[]): RunwaySummary => {
	const lookbackMs = 14 * 24 * 60 * 60 * 1000;
	const cutoff = Date.now() - lookbackMs;
	let totalBurn = 0;
	transactions.forEach((tx) => {
		if (transactionTypeMeta(tx.transactionType).key !== 'debit') return;
		const ts = new Date(tx.createdAt).getTime();
		if (Number.isNaN(ts) || ts < cutoff) return;
		totalBurn += Math.abs(tx.amount);
	});

	const dailyBurn = totalBurn / 14;

	if (dailyBurn <= 0) {
		const level: RunwaySummary['level'] = currentBalance >= 100 ? 'idle' : currentBalance >= 50 ? 'low' : 'critical';
		const label = level === 'idle' ? 'No recent activity' : level === 'low' ? 'Top up soon' : 'Top up needed';
		return { dailyBurn: 0, runwayDays: null, level, label };
	}

	const runwayDays = currentBalance / dailyBurn;
	let level: RunwaySummary['level'];
	if (currentBalance < 50 || runwayDays < RUNWAY_LOW_DAYS) level = 'critical';
	else if (runwayDays < RUNWAY_HEALTHY_DAYS) level = 'low';
	else level = 'healthy';

	const label = level === 'critical' ? 'Critical balance' : level === 'low' ? 'Running low' : 'Healthy balance';

	return { dailyBurn, runwayDays, level, label };
};

export const buildSparklineSeries = (transactions: IWalletTransaction[], points = SPARKLINE_POINTS): number[] => {
	if (!transactions.length) return [];
	const ordered = [...transactions]
		.filter((tx) => Number.isFinite(tx.balanceAfter))
		.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
		.slice(-points)
		.map((tx) => tx.balanceAfter);
	return ordered;
};

export const serviceTypeOptions = (transactions: IWalletTransaction[]): { value: string; label: string }[] => {
	const set = new Set<string>();
	transactions.forEach((tx) => {
		if (tx.serviceType) set.add(tx.serviceType);
	});
	return Array.from(set)
		.sort((a, b) => a.localeCompare(b))
		.map((value) => ({ value, label: humanizeServiceType(value) }));
};

// Title-casing would otherwise turn `SMS_BROADCAST` into "Sms Broadcast".
const SERVICE_ACRONYMS = new Set(['sms', 'ussd', 'ai', 'otp']);

export const humanizeServiceType = (raw: string) => {
	if (!raw) return 'Unknown';
	return raw
		.replace(/[_-]+/g, ' ')
		.toLowerCase()
		.replace(/\b\w+/g, (word) =>
			SERVICE_ACRONYMS.has(word) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)
		);
};

export const isoDayStart = (date: Date) => startOfDayUtc(date).toISOString();

export const daysAgoIso = (days: number) => {
	const d = new Date();
	d.setUTCHours(0, 0, 0, 0);
	d.setUTCDate(d.getUTCDate() - days);
	return d.toISOString();
};

export const transactionMatchesSearch = (tx: IWalletTransaction, query: string): boolean => {
	if (!query) return true;
	const needle = query.toLowerCase().trim();
	if (!needle) return true;
	const haystacks = [tx.reference, tx.serviceType, tx.id, TRANSACTION_TYPE_LABELS[tx.transactionType] ?? '', String(tx.amount)];
	return haystacks.some((value) => value?.toLowerCase().includes(needle));
};

export const sortTransactions = (
	items: IWalletTransaction[],
	sort: 'date-desc' | 'date-asc' | 'amount-desc' | 'amount-asc'
): IWalletTransaction[] => {
	const copy = [...items];
	copy.sort((a, b) => {
		switch (sort) {
			case 'date-asc':
				return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
			case 'amount-desc':
				return Math.abs(b.amount) - Math.abs(a.amount);
			case 'amount-asc':
				return Math.abs(a.amount) - Math.abs(b.amount);
			case 'date-desc':
			default:
				return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
		}
	});
	return copy;
};
